// The single tokenizer for expected-reading text on the local-ASR path.
//
// Two consumers must agree, or words silently vanish from transcripts:
//   1. vosk/voskGrammar.js — builds the recognizer's word-list grammar, so a token
//      that does not match a `graph/words.txt` symbol is dropped by vosk (it logs
//      "Ignoring word missing in vocabulary", recognizer.cc:84, and never throws).
//   2. engine/streaming_consensus.js — builds the in-passage `wordSet`, so a token
//      that does not match the DECODED word is rewritten to `<unk>` and filtered
//      out of the emitted transcript entirely.
//
// They were separate hand-rolled copies with a "mirrors the other" comment, and
// they drifted: the grammar side was fixed to keep word-internal apostrophes while
// the wordSet side still stripped them, so a correctly-decoded "don't" landed
// against a wordSet holding "dont" and was discarded as out-of-passage. One
// implementation, imported by both, is the only version of this that stays true.
//
// This is a direct port of amira-lm-build's `clean_phrase` (src/text.rs), which is
// the source of truth: it is the transform the graph vocabulary was keyed by. The
// arm order below mirrors the Rust match, and the order matters — `-` and `'` are
// both ASCII punctuation but are claimed by earlier arms, and U+2019 is inside the
// curly-quote range but is claimed by the apostrophe arm.
//
// Confirmed properties of the shipped `graph/words.txt` (-zoov-r4, byte-identical to
// -r3's — the r4 repack changed packaging only, not the symbol table): symbols are
// LOWERCASE, and non-ASCII characters are present and NOT ASCII-folded (`piñata`
// is stored as `piñata`). Hence the lower-casing here, and hence non-ASCII letters
// are kept rather than deleted — deleting them produced `piata`, which matches no
// symbol, on exactly the Spanish-loanword class the zero-OOV rebuild set out to fix.

/**
 * True for a single alphanumeric, Unicode-aware to match Rust's
 * `char::is_alphanumeric()` (= `is_alphabetic() || is_numeric()`, i.e. the
 * Alphabetic derived property plus the Nd/Nl/No categories).
 *
 * ASCII-only would break twice over on accented words: `José's` would lose its
 * apostrophe (because `é` failed the neighbour test) AND lose the `é`, yielding
 * `joses` where clean_phrase yields `josé's`. Property escapes are expanded to
 * explicit ranges by @babel/preset-env, so this is safe even on engines older
 * than the SIMD floor local ASR requires.
 *
 * Guarded because string indexing past either end yields undefined, and testing a
 * regex against undefined would coerce it to the string "undefined" and match.
 * @param {string|undefined} ch
 * @returns {boolean}
 */
function isAlnum(ch) {
  return typeof ch === 'string' && /^[\p{Alphabetic}\p{N}]$/u.test(ch);
}

/**
 * Mirrors Rust's `char::is_ascii_punctuation()`: the four ASCII punctuation runs.
 * Spelled as code points because the equivalent character class needs three
 * ranges whose endpoints are themselves class metacharacters.
 * @param {string} ch
 * @returns {boolean}
 */
function isAsciiPunct(ch) {
  const c = ch.charCodeAt(0);
  return (c >= 0x21 && c <= 0x2f)   // ! " # $ % & ' ( ) * + , - . /
    || (c >= 0x3a && c <= 0x40)     // : ; < = > ? @
    || (c >= 0x5b && c <= 0x60)     // [ \ ] ^ _ `
    || (c >= 0x7b && c <= 0x7e);    // { | } ~
}

/**
 * Lower-cases a word and strips punctuation exactly as `clean_phrase` does:
 * the hyphen is kept, unicode figure/en/em/bar dashes (U+2012..U+2015) become a
 * space (splitting a joined "word—word"), word-internal apostrophes are kept,
 * curly quotes / primes / ellipsis and every other ASCII punctuation mark are
 * deleted IN PLACE (no break inserted, so "cat,dog" -> "catdog"), and everything
 * else — including accented letters — is preserved.
 *
 * Word-internal apostrophes are LEXICAL, not punctuation: every Vosk word table
 * holds `don't` and `o'clock`, so stripping them manufactures OOV —
 * amira-lm-build#42 measured 2,569 tokens / 407 unique words ("you've",
 * "hadn't", "family's") decoding as [unk] while the vocabulary had the word.
 * Curly `’` (U+2019) normalizes to ASCII `'`, matching clean_phrase, which pushes
 * an ASCII apostrophe for both. An apostrophe at a word EDGE is a quote mark and
 * drops ("'tis" -> "tis", "rock'" -> "rock"). U+2018 is NOT an apostrophe here:
 * clean_phrase's drop arm claims it, so it is deleted in place.
 *
 * Divergence from clean_phrase, deliberate: a token containing no alphanumeric at
 * all (e.g. "---", which the Rust returns verbatim) reduces to '' — such a token
 * could only ever be OOV in the grammar and out-of-passage in the wordSet.
 * @param {string} word
 * @returns {string}
 */
export function normalizeWord(word) {
  const lower = String(word || '').toLowerCase();
  let kept = '';
  for (let i = 0; i < lower.length; i += 1) {
    const ch = lower[i];
    // Hyphen-minus is kept (clean_phrase excludes '-' from the strip set).
    if (ch === '-') {
      kept += '-';
      continue;
    }
    // Figure/en/em/horizontal-bar dashes split a joined "word—word" into two.
    if (ch >= '‒' && ch <= '―') {
      kept += ' ';
      continue;
    }
    // Apostrophe (ASCII or curly): kept word-internal, dropped at edges. Both
    // neighbours are read off the INPUT, not the output built so far, because that is
    // what the Rust does:
    //   '\'' | '\u{2019}' => { if prev_alnum && chars.peek()
    //                              .is_some_and(|c| c.is_alphanumeric()) { ... } }
    //   ...
    //   prev_alnum = ch.is_alphanumeric();   // runs for EVERY char, dropped or not
    // So it peeks the next input char (not a retroactive decision), and a dropped
    // character still resets prev_alnum — hence "a,'b" -> "ab", not "a'b".
    if (ch === "'" || ch === '’') {
      if (isAlnum(lower[i - 1]) && isAlnum(lower[i + 1])) {
        kept += "'";
      }
      continue;
    }
    // Remaining curly quotes, primes, and ellipsis -> drop.
    if ((ch >= '‘' && ch <= '‟') || ch === '…' || ch === '′' || ch === '″') {
      continue;
    }
    // Every other ASCII punctuation mark -> drop.
    if (isAsciiPunct(ch)) {
      continue;
    }
    kept += ch;
  }
  return kept
    .split(/\s+/)
    .filter((token) => /[\p{Alphabetic}\p{N}]/u.test(token))
    .join(' ');
}

/**
 * Whether a RAW transcript token is the unknown-word marker, in either spelling the pipeline
 * produces: vosk emits `[unk]`, and voskAsr's streamResult maps it to the server-style
 * `<UNK>` for ReaderManager's suppression scan. Lives here so the spelling has one home —
 * it was previously encoded in three places, which is the drift this module exists to stop.
 * Must run BEFORE normalizeWord, which strips the brackets and leaves the bare word `unk`.
 * @param {string} token
 * @returns {boolean}
 */
export function isUnkToken(token) {
  return /^(<unk>|\[unk\])$/i.test(String(token || ''));
}
